/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (CURRENT) - Initial creation of the IndexedDB utility for chat conversations.
 *
 * -----
 * SECURITY_RISK: LOW - This module provides access to IndexedDB for storing chat conversations.
 *                      It does not store sensitive user data or API keys directly, but the conversation
 *                      content itself might be considered sensitive depending on user input.
 * INTENT: Centralize and standardize IndexedDB interactions for chat message persistence,
 *         ensuring consistent data access and user experience across page reloads or tab changes.
 * PROMPTER_INSTRUCTIONS: Ensure all IndexedDB operations are robust, handle errors gracefully,
 *                        and are aligned with the application's data persistence strategy.
 * LLM_CONFIDENCE: 100% - Standard IndexedDB utility implementation.
 */
import { ChatMessage } from '../types/chat.d';

const CHAT_DB_NAME = 'aiAssistantChatDB';
const CHAT_STORE_NAME = 'conversations';
const CONVERSATION_KEY = 'currentConversation'; // The key under which the conversation array is stored

/**
 * Opens the IndexedDB database for chat conversations and creates the object store if it doesn't exist.
 * @returns A Promise that resolves with the IDBDatabase object.
 */
export const openChatDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      console.warn("IndexedDB not available in this environment for chat conversations.");
      reject(new Error("IndexedDB not available"));
      return;
    }

    const request = indexedDB.open(CHAT_DB_NAME, 1);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(CHAT_STORE_NAME)) {
        db.createObjectStore(CHAT_STORE_NAME);
      }
    };

    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event) => {
      console.error("Error opening chat IndexedDB:", (event.target as IDBOpenDBRequest).error);
      reject((event.target as IDBOpenDBRequest).error);
    };
  });
};

/**
 * Retrieves the chat conversation stored under the CONVERSATION_KEY.
 * @returns A Promise that resolves with an array of ChatMessage, or an empty array if not found.
 */
export const getConversation = async (): Promise<ChatMessage[]> => {
  try {
    const db = await openChatDB();
    const transaction = db.transaction(CHAT_STORE_NAME, 'readonly');
    const store = transaction.objectStore(CHAT_STORE_NAME);
    const request = store.get(CONVERSATION_KEY);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        resolve(request.result || []);
      };
      request.onerror = (event) => {
        console.error("Error getting conversation from IndexedDB:", (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };
    });
  } catch (error) {
    console.error("Error accessing IndexedDB for conversation:", error);
    return []; // Return empty array on error
  }
};

/**
 * Saves the current chat conversation to IndexedDB under the CONVERSATION_KEY.
 * @param conversation The array of ChatMessage to save.
 * @returns A Promise that resolves when the conversation is saved.
 */
export const saveConversation = async (conversation: ChatMessage[]): Promise<void> => {
  try {
    const db = await openChatDB();
    const transaction = db.transaction(CHAT_STORE_NAME, 'readwrite');
    const store = transaction.objectStore(CHAT_STORE_NAME);

    store.put(conversation, CONVERSATION_KEY);

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = (event) => {
        console.error("Error saving conversation to IndexedDB:", (event.target as IDBTransaction).error);
        reject((event.target as IDBTransaction).error);
      };
    });
  } catch (error) {
    console.error("Error accessing IndexedDB for saving conversation:", error);
    throw error;
  }
};

/**
 * Clears the chat conversation from IndexedDB.
 * @returns A Promise that resolves when the conversation is cleared.
 */
export const clearConversation = async (): Promise<void> => {
  try {
    const db = await openChatDB();
    const transaction = db.transaction(CHAT_STORE_NAME, 'readwrite');
    const store = transaction.objectStore(CHAT_STORE_NAME);

    store.delete(CONVERSATION_KEY);

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = (event) => {
        console.error("Error clearing conversation from IndexedDB:", (event.target as IDBTransaction).error);
        reject((event.target as IDBTransaction).error);
      };
    });
  } catch (error) {
    console.error("Error accessing IndexedDB for clearing conversation:", error);
    throw error;
  }
};